<template>
  <div class="grid gap-x-4 gap-y-2 md:grid-cols-2">
    <div
      ref="startField"
      class="flex cursor-pointer flex-col gap-1"
      data-testid="start-date"
      @click="toggleEdge('start')"
    >
      <h4>{{ startDateTitle }}</h4>
      <span
        :class="input({ size: 'sm', class: 'font-normal' })"
        role="textbox"
        :data-focus="showPicker && activeEdge === 'start' ? '' : undefined"
        data-testid="start-time"
        >{{ formattedStartDate }}</span
      >
    </div>

    <div
      ref="endField"
      class="flex cursor-pointer flex-col gap-1"
      data-testid="end-date"
      @click="toggleEdge('end')"
    >
      <h4>{{ endDateTitle }}</h4>
      <span
        :class="input({ size: 'sm', class: 'font-normal' })"
        role="textbox"
        :data-focus="showPicker && activeEdge === 'end' ? '' : undefined"
        data-testid="end-time"
        >{{ formattedEndDate }}</span
      >
    </div>

    <div v-if="showPicker" ref="pickerContainer" :style="floatingStyles" class="z-[1100]">
      <Datepicker
        v-model="dates"
        v-model:active-edge="activeEdge"
        range
        inline
        week-numbers
        :clearable="false"
        enable-time-picker
        :disabled-dates="disabledDates"
        :highlighted-dates="highlightedDates"
        :min-date="minDate"
        :max-date="maxDate"
        :no-disabled-range="noDisabledRange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '@/hooks/use-formatted-date';
import { computed, useTemplateRef } from 'vue';
import { useRangePickerFields } from '@/components/datepicker/use-range-picker-fields';
import Datepicker from '@/components/Datepicker.vue';
import { input } from '@carvia/theme/lib/cva/input.ts';

const props = defineProps<{
  startDate: Date | string;
  endDate: Date | string;
  bookingId?: string;
  disabled?: boolean;
  startDateTitle: string;
  endDateTitle: string;
  disabledDates?: ((date: Date) => boolean) | Date[];
  highlightedDates?: (date: Date) => boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
  noDisabledRange?: boolean;
}>();
const emit = defineEmits<{
  (e: 'update:startDate', value: string): void;
  (e: 'update:endDate', value: string): void;
  (e: 'change', startDate: string, endDate: string): void;
}>();

const pickerContainer = useTemplateRef<HTMLElement>('pickerContainer');
const startField = useTemplateRef<HTMLElement>('startField');
const endField = useTemplateRef<HTMLElement>('endField');

const { dates, showPicker, activeEdge, toggleEdge, floatingStyles } = useRangePickerFields({
  startDate: () => props.startDate,
  endDate: () => props.endDate,
  disabled: () => props.disabled,
  pickerContainer,
  fields: [startField, endField],
  onCommit: (startDate, endDate) => {
    emit('update:startDate', startDate.toISOString());
    emit('update:endDate', endDate.toISOString());
    emit('change', startDate.toISOString(), endDate.toISOString());
  },
});

const formattedStartDate = computed(() => formatDate(dates.value[0]));
const formattedEndDate = computed(() => formatDate(dates.value[1]));
</script>
